import { ComponentProps } from "react";

import { useT } from "@/i18n";
import { BasePlatform, PlatformReference } from "@/types/platform.types";
import { getIconSize, IconSize, Tooltip, useTheme } from "@/ui";
import { cn } from "@/utils/cn";
import { getAvatarPlaceholderUrl } from "@/utils/common";

export type PlatformLogoProps = Omit<ComponentProps<"img">, "src" | "alt" | "aria-label"> & {
  platform: BasePlatform | PlatformReference;
  size?: number | IconSize;
  withTooltip?: boolean;
};

const isBasePlatform = (platform: BasePlatform | PlatformReference): platform is BasePlatform =>
  "lightLogo" in platform;

export const PlatformLogo = ({
  platform,
  className,
  size,
  withTooltip,
  width,
  height,
  ...props
}: PlatformLogoProps) => {
  const t = useT();
  const { theme } = useTheme();
  const sizePx = getIconSize(size);

  const label = t("platform.components.logo.label", {
    name: platform.title,
  });

  const src = isBasePlatform(platform)
    ? (theme === "dark" ? platform.lightEmblem : platform.darkEmblem) ||
      (theme === "dark" ? platform.lightLogo : platform.darkLogo) ||
      getAvatarPlaceholderUrl(platform.title)
    : platform.logo || getAvatarPlaceholderUrl(platform.title);

  const content = (
    <img
      alt={label}
      aria-label={label}
      style={{ width: width ?? sizePx, height: height ?? sizePx }}
      className={cn("shrink-0 rounded-sm object-contain", className)}
      src={src}
      {...props}
    />
  );

  return withTooltip ? <Tooltip content={platform.title}>{content}</Tooltip> : content;
};
